import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";
import {
  PartnerSecurityTrialRequestStatusSchema,
  supportedLanguages,
  userInfoKeys,
  type SupportedLanguage,
} from "./userInfo";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/enterprise-customer`;

const keys = {
  all: () => ["enterpriseCustomer"] as const,
  customer: () => [...keys.all(), "customer"] as const,
  organisations: () => [...keys.all(), "organisations"] as const,
  userSelectOptions: () => [...keys.all(), "userSelectOptions"] as const,
  userAdmins: () => [...keys.all(), "userAdmins"] as const,
} as const;

const CountryCodeSchema = z.enum(["SE", "NO"]);
export const ENTERPRISE_CUSTOMER_COUNTRY_CODES = CountryCodeSchema.options;
export type EnterpriseCustomerCountryCode = z.infer<typeof CountryCodeSchema>;

export const ENTERPRISE_CUSTOMER_ORGANISATION_NAME_ALREADY_EXISTS = "ERROR_TENANT_ID_ALREADY_ADDED";

const EnterpriseCustomerSchema = z.object({
  countryCode: CountryCodeSchema,
  language: z.enum(supportedLanguages),
});

export const enterpriseCustomerQueryOptions = () =>
  queryOptions({
    queryKey: keys.customer(),
    queryFn: () => api.get(BASE_ENDPOINT, { schema: EnterpriseCustomerSchema }),
    throwOnError: true,
  });

const ProductAdminUserSchema = z.object({
  userId: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});
export type ProductAdminUser = z.infer<typeof ProductAdminUserSchema>;

const EnterpriseCustomerOrganisationSchema = z.object({
  id: z.string(),
  organizationName: z.string(),
  organizationNumber: z.string(),
  countryCode: CountryCodeSchema,
  language: z.enum(supportedLanguages),
  hasPublicApiIntegration: z.boolean(),
  deletedDate: z.string().nullable(),
  partnerSecurityTrialRequestStatus: PartnerSecurityTrialRequestStatusSchema,
  productAdmins: z.array(ProductAdminUserSchema),
});
export type EnterpriseCustomerOrganisation = z.infer<typeof EnterpriseCustomerOrganisationSchema>;

export const enterpriseCustomerOrganisationsQueryOptions = () =>
  queryOptions({
    queryKey: keys.organisations(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/organisations`, { schema: z.array(EnterpriseCustomerOrganisationSchema) }),
    throwOnError: true,
  });

export type CreateEnterpriseCustomerOrganisationPayload = {
  organizationName: string;
  organizationNumber: string;
  countryCode: EnterpriseCustomerCountryCode;
  language: SupportedLanguage;
};

export const createEnterpriseCustomerOrganisationMutationOptions = () =>
  mutationOptions({
    mutationFn: (payload: CreateEnterpriseCustomerOrganisationPayload) =>
      api.post(`${BASE_ENDPOINT}/organisations`, payload, { schema: EnterpriseCustomerOrganisationSchema }),
    onSuccess: () => invalidateAll({ blocking: [userInfoKeys.user()] }),
  });

export type UpdateEnterpriseCustomerOrganisationPayload = Partial<{
  organizationName: string;
  organizationNumber: string;
  countryCode: EnterpriseCustomerCountryCode;
  language: SupportedLanguage;
}>;

export const updateEnterpriseCustomerOrganisationMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateEnterpriseCustomerOrganisationPayload) =>
      api.patch(`${BASE_ENDPOINT}/organisations/${id}`, payload, {
        schema: EnterpriseCustomerOrganisationSchema,
      }),
    onSuccess: () => invalidateAll({ blocking: [userInfoKeys.user()] }),
  });

export const deleteEnterpriseCustomerOrganisationMutationOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => api.delete(`${BASE_ENDPOINT}/organisations/${id}`),
    onSuccess: () => invalidateAll({ blocking: [userInfoKeys.user()] }),
  });

export const enterpriseCustomerUserSelectOptionsQueryOptions = () =>
  queryOptions({
    queryKey: keys.userSelectOptions(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/users/select-options`, { schema: z.array(ProductAdminUserSchema) }),
    throwOnError: true,
  });

export const enterpriseCustomerUserAdminsQueryOptions = () =>
  queryOptions({
    queryKey: keys.userAdmins(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/users/admins`, { schema: z.array(ProductAdminUserSchema) }),
    throwOnError: true,
  });

export const userEmailLookupMutationOptions = () =>
  mutationOptions({
    mutationFn: (email: string) =>
      api.get(`${FORMS_API_BASE_ENDPOINT}/users/email-lookup/${encodeURIComponent(email)}`, {
        schema: ProductAdminUserSchema.nullable(),
      }),
  });

export type UpdateProductAdminsPayload = { organisationId: string; userIds: string[] };

export const updateProductAdminsMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ organisationId, userIds }: UpdateProductAdminsPayload) =>
      api.put(`${BASE_ENDPOINT}/organisations/${organisationId}/product-admins`, { userIds }),
    onSuccess: () => invalidateAll({ blocking: [userInfoKeys.user()] }),
  });

export const PRODUCT_ADMIN_USER_ALREADY_MEMBER_OF_CUSTOMER = "USER_ALREADY_MEMBER_OF_CUSTOMER";

type CreateProductAdminPayload = { email: string; firstName: string; lastName: string };

export const createProductAdminMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ organisationId, ...payload }: { organisationId: string } & CreateProductAdminPayload) =>
      api.post(`${BASE_ENDPOINT}/organisations/${organisationId}/product-admins/new-user`, payload),
    onSuccess: () => invalidateAll({ blocking: [userInfoKeys.user()] }),
  });
